import { useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { IStudioWizardFormModel } from "../models/studioWizardFormModel";
import { MAX_RESERVATION_NOTIFICATION_EMAILS } from "../models/reservationNotificationEmails";

const FIELD_NAME = "reservationNotificationEmails" as const;

/**
 * Add/remove logic for the "notify these mailboxes about a new reservation"
 * rows in the studio wizard. Each row is a plain DefaultInputField bound to
 * `reservationNotificationEmails.<index>`; this hook only owns the shape of
 * the array. There is always at least one row so the user has somewhere to
 * type, without that empty row being written into the form until touched.
 */
export const useReservationNotificationEmails = (
  form: UseFormReturn<IStudioWizardFormModel>,
) => {
  const value = useWatch({ control: form.control, name: FIELD_NAME });

  const emails = useMemo(
    () => (value?.length ? value.map((email) => email ?? "") : [""]),
    [value],
  );

  const commit = (next: string[]) =>
    form.setValue(FIELD_NAME, next, {
      shouldDirty: true,
      // Re-validate only once the user has already hit save and seen the
      // errors, so the form doesn't shout at a half-typed address.
      shouldValidate: form.formState.isSubmitted,
    });

  const addEmail = () => commit([...emails, ""]);

  const removeEmail = (index: number) => {
    const next = emails.filter((_, i) => i !== index);
    commit(next.length ? next : [""]);
  };

  return {
    emails,
    fieldName: FIELD_NAME,
    addEmail,
    removeEmail,
    canAddEmail: emails.length < MAX_RESERVATION_NOTIFICATION_EMAILS,
    // With a single row there is nothing to remove - clearing it is what the
    // input itself is for - so the button is hidden rather than disabled.
    canRemoveEmail: emails.length > 1,
  };
};
